"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useImagineTheme } from "@/web-components";
import { cn } from "@/lib/utils";

// Types
type AnimationType = "default" | "typing" | "submit" | "constant";

interface Circle {
  id: string;
  x: number;
  y: number;
  originalRadius: number;
  currentRadius: number;
  distance: number;
  ringNumber: number;
  canTwinkle: boolean;
  minRadius: number;
  twinklePhase: number;
}

interface AnimatedLogoProps {
  size: number;
  animation?: AnimationType;
  className?: string;
  forceTheme?: "light" | "dark";
}

export function AnimatedLogo({
  size,
  animation = "default",
  className,
  forceTheme,
}: AnimatedLogoProps) {
  const themeFromHook = useImagineTheme();
  const theme = forceTheme ?? themeFromHook;
  const [currentTime, setCurrentTime] = useState(Date.now());
  const animationStartTimeRef = useRef(Date.now());
  const animationFrameRef = useRef<number | null>(null);

  // Avoid SSR/CSR mismatch: next-themes only resolves on client after mount
  // useEffect(() => {
  //   setMounted(true);
  // }, []);

  // Animation configuration
  const animationConfig = useMemo(
    () => ({
      typing: {
        pulseSpeed: 0.3,
        rippleDelayFactor: 0.002,
        ringDelayFactor: 0.13,
        minScale: 0.95,
        maxScale: 1.15,
        smallDotMinScale: 0.4,
        smallDotMaxScale: 1.25,
        mediumDotMinScale: 0.7,
        mediumDotMaxScale: 1.3,
      },
      default: {
        twinkleSpeed: 4.5,
      },
      submit: {
        pulseSpeed: 0.3,
        rippleDelayFactor: 0.002,
        ringDelayFactor: 0.13,
        minScale: 0.05,
        maxScale: 1.2,
        smallDotMinScale: 0.01,
        smallDotMaxScale: 1.5,
        mediumDotMinScale: 0.1,
        mediumDotMaxScale: 1.3,
      },
      constant: {
        pulseSpeed: 0.3,
        rippleDelayFactor: 0.002,
        ringDelayFactor: 1,
        minScale: 0.05,
        maxScale: 1.2,
        smallDotMinScale: 0.01,
        smallDotMaxScale: 1.5,
        mediumDotMinScale: 0.1,
        mediumDotMaxScale: 1.3,
      },
    }),
    [],
  );

  const gridSpacing = 26;
  const centerX = 150;
  const centerY = 150;
  const gridStart = 7;

  // Helper function to get distance-based radius
  const getDistanceBasedRadius = useCallback((x: number, y: number): number => {
    const dx = x - centerX;
    const dy = y - centerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= 30) return 16;
    else if (distance <= 85) return 15;
    else if (distance <= 100) return 14;
    else if (distance <= 115) return 12;
    else if (distance <= 130) return 11;
    else if (distance <= 145) return 8;
    else if (distance <= 160) return 7;
    else if (distance <= 175) return 5;
    else if (distance <= 190) return 3;
    else return 0;
  }, []);

  // Main function to determine circle radius
  const getRadius = useCallback(
    (x: number, y: number, col: number, row: number): number => {
      const distFromEdgeCol = Math.min(col, 11 - col);
      const distFromEdgeRow = Math.min(row, 11 - row);
      const minDistFromEdge = Math.min(distFromEdgeCol, distFromEdgeRow);

      // Ring 0: Outermost
      if (minDistFromEdge === 0) {
        if (distFromEdgeCol === 0) {
          if (row === 4 || row === 7) return 3;
          if (row === 5 || row === 6) return 5;
          return 0;
        }
        if (distFromEdgeRow === 0) {
          if (col === 4 || col === 7) return 3;
          if (col === 5 || col === 6) return 5;
          return 0;
        }
      }

      // Ring 1: Second outermost
      if (minDistFromEdge === 1) {
        if (distFromEdgeCol === 1) {
          if (row < 2 || row > 9) return 0;
          if (row === 2 || row === 9) return 5;
          return getDistanceBasedRadius(x, y);
        }
        if (distFromEdgeRow === 1) {
          if (col < 2 || col > 9) return 0;
          if (col === 2 || col === 9) return 5;
          return getDistanceBasedRadius(x, y);
        }
      }

      return getDistanceBasedRadius(x, y);
    },
    [getDistanceBasedRadius],
  );

  // Initialize circles
  const initializeCircles = useCallback((): Array<Circle> => {
    const newCircles: Array<Circle> = [];

    for (let row = 0; row < 12; row++) {
      for (let col = 0; col < 12; col++) {
        const x = gridStart + col * gridSpacing;
        const y = gridStart + row * gridSpacing;
        const radius = getRadius(x, y, col, row);

        if (radius > 0) {
          const dx = x - centerX;
          const dy = y - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);

          // Calculate ring number based on distance
          let ringNumber: number;
          if (distance <= 85) ringNumber = 0;
          else if (distance <= 100) ringNumber = 1;
          else if (distance <= 115) ringNumber = 2;
          else if (distance <= 130) ringNumber = 3;
          else if (distance <= 145) ringNumber = 4;
          else if (distance <= 160) ringNumber = 5;
          else if (distance <= 175) ringNumber = 6;
          else ringNumber = 7;

          // Determine if this dot should twinkle
          const canTwinkle: boolean =
            radius <= 12 && (radius <= 8 || radius >= 11);
          let minRadius: number = radius;
          if (canTwinkle) {
            if (radius === 3) minRadius = 1;
            else if (radius === 5) minRadius = 3;
            else if (radius === 7) minRadius = 5;
            else if (radius === 8) minRadius = 6;
            else if (radius === 11) minRadius = 9;
            else if (radius === 12) minRadius = 10;
          }

          newCircles.push({
            id: `circle-${row}-${col}`,
            x,
            y,
            originalRadius: radius,
            currentRadius: radius,
            distance,
            ringNumber,
            canTwinkle,
            minRadius,
            twinklePhase: Math.random() * Math.PI * 2,
          });
        }
      }
    }

    return newCircles;
  }, [getRadius]);

  const [baseCircles] = useState<Array<Circle>>(() => initializeCircles());

  // Calculate animated radius for a circle
  const getAnimatedRadius = useCallback(
    (circle: Circle, elapsed: number): number => {
      const {
        originalRadius,
        distance,
        ringNumber,
        canTwinkle,
        minRadius,
        twinklePhase,
      } = circle;

      switch (animation) {
        case "default":
          if (canTwinkle) {
            const twinkleFactor =
              (Math.sin(
                elapsed * animationConfig.default.twinkleSpeed + twinklePhase,
              ) +
                1) /
              2;
            return minRadius + (originalRadius - minRadius) * twinkleFactor;
          }
          return originalRadius;

        case "typing": {
          const config = animationConfig.typing;
          const distanceDelay = distance * config.rippleDelayFactor;
          const ringDelay = ringNumber * config.ringDelayFactor;
          const totalDelay = distanceDelay + ringDelay;
          const adjustedTime = Math.max(0, elapsed - totalDelay);

          if (adjustedTime > 0) {
            const rippleFactor =
              (Math.sin(adjustedTime * Math.PI * 2 * config.pulseSpeed) + 1) /
              2;
            let minScale = config.minScale;
            let maxScale = config.maxScale;

            if (originalRadius <= 5) {
              minScale = config.smallDotMinScale;
              maxScale = config.smallDotMaxScale;
            } else if (originalRadius <= 8) {
              minScale = config.mediumDotMinScale;
              maxScale = config.mediumDotMaxScale;
            }

            return (
              originalRadius * (minScale + (maxScale - minScale) * rippleFactor)
            );
          }
          return circle.currentRadius;
        }

        case "submit": {
          const config = animationConfig[animation];
          const distanceDelay = distance * config.rippleDelayFactor;
          const ringDelay = ringNumber * config.ringDelayFactor;
          const totalDelay = distanceDelay + ringDelay;
          const adjustedTime = Math.max(0, elapsed - totalDelay);

          if (adjustedTime > 0) {
            const rippleFactor =
              (Math.sin(adjustedTime * Math.PI * 2 * config.pulseSpeed) + 1) /
              2;
            let minScale = config.minScale;
            let maxScale = config.maxScale;

            if (originalRadius <= 5) {
              minScale = config.smallDotMinScale;
              maxScale = config.smallDotMaxScale;
            } else if (originalRadius <= 8) {
              minScale = config.mediumDotMinScale;
              maxScale = config.mediumDotMaxScale;
            }

            return (
              originalRadius * (minScale + (maxScale - minScale) * rippleFactor)
            );
          }
          return circle.currentRadius;
        }

        case "constant": {
          const config = animationConfig[animation];

          const rippleFactor =
            (Math.sin(elapsed * Math.PI * 2 * config.pulseSpeed) + 1) / 2;
          let minScale = config.minScale;
          let maxScale = config.maxScale;

          if (originalRadius <= 5) {
            minScale = config.smallDotMinScale;
            maxScale = config.smallDotMaxScale;
          } else if (originalRadius <= 8) {
            minScale = config.mediumDotMinScale;
            maxScale = config.mediumDotMaxScale;
          }

          return (
            originalRadius * (minScale + (maxScale - minScale) * rippleFactor)
          );
        }

        default:
          return originalRadius;
      }
    },
    [animation, animationConfig],
  );

  // Start animation loop once
  useEffect(() => {
    let isRunning = true;

    const updateTime = () => {
      if (!isRunning) return;
      setCurrentTime(Date.now());
      // Use setTimeout as fallback to ensure it keeps running
      setTimeout(() => {
        if (isRunning) {
          animationFrameRef.current = requestAnimationFrame(updateTime);
        }
      }, 1000 / 60); // 60fps fallback
    };

    // Start immediately
    updateTime();

    return () => {
      isRunning = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []); // Only run once on mount

  // Reset start time when animation changes (like Svelte $effect)
  useEffect(() => {
    animationStartTimeRef.current = Date.now();
  }, [animation]);

  const elapsedTime = (currentTime - animationStartTimeRef.current) / 1000;

  // Calculate current circles with animated radii
  const circles = baseCircles.map((circle) => ({
    ...circle,
    currentRadius: getAnimatedRadius(circle, elapsedTime),
  }));

  const background = "transparent";
  const isDark = theme === "dark";
  const foreground = isDark ? "white" : "black";

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 300 300"
      className={cn("block", className)}
    >
      <rect width="300" height="300" fill={background} />

      <defs>
        <mask id="halftone-mask">
          <rect width="300" height="300" fill="white" />
          <circle cx="150" cy="150" r="70" fill="black" />
        </mask>
      </defs>

      <g mask="url(#halftone-mask)">
        {circles.map((circle) => (
          <circle
            key={circle.id}
            cx={circle.x}
            cy={circle.y}
            r={circle.currentRadius}
            fill={foreground}
            style={{ transition: "r 0.05s linear" }}
          />
        ))}
      </g>
    </svg>
  );
}
